import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Star, CheckCircle2, Heart, ExternalLink } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const Stars = ({ value, onChange, testid }) => (
  <div className="flex gap-1.5" data-testid={testid}>
    {[1, 2, 3, 4, 5].map((n) => (
      <button key={n} type="button" onClick={() => onChange(n)} data-testid={`${testid}-${n}`}
        className="p-1 transition-transform hover:scale-110">
        <Star size={30} className={n <= value ? "fill-[#D98E73] text-[#D98E73]" : "text-[#E5E0D8]"} />
      </button>
    ))}
  </div>
);

export default function FeedbackSurveyPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [experience, setExperience] = useState(0);
  const [ret, setRet] = useState(0);
  const [comments, setComments] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [quote, setQuote] = useState("");
  const [tName, setTName] = useState("");
  const [tDone, setTDone] = useState(false);
  const [tSaving, setTSaving] = useState(false);

  const { data: survey, isLoading, error } = useQuery({
    queryKey: ["fb-survey", token],
    queryFn: () => api.get("/public/feedback-survey", { params: { token } }).then((r) => r.data),
    enabled: !!token, retry: false,
  });

  const submit = async () => {
    if (!experience) { toast.error("Please tap a star rating first."); return; }
    setSending(true);
    try {
      const { data } = await api.post("/public/feedback-survey", { token, experience, return_likelihood: ret, comments });
      setResult(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const sendTestimonial = async () => {
    if (quote.trim().length < 5) { toast.error("Please share a few words first."); return; }
    setTSaving(true);
    try {
      await api.post("/public/feedback-survey/testimonial", { token, quote, name: tName });
      setTDone(true);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setTSaving(false); }
  };

  if (!token || error) return (
    <div className="min-h-[60vh] grid place-items-center px-6" data-testid="survey-invalid">
      <div className="text-center"><p className="font-serif text-2xl text-[#1A242B]">This feedback link isn't valid.</p>
        <p className="text-[#4A5D6B] mt-2">It may have expired. You can always reach us at <a className="text-[#144F75] underline" href="mailto:info@enhancingyourworld.com">info@enhancingyourworld.com</a>.</p></div>
    </div>
  );
  if (isLoading) return <div className="min-h-[60vh] grid place-items-center"><Loader2 className="animate-spin text-[#144F75]" size={30} /></div>;

  const done = result || (survey?.responded ? { positive: survey.positive, google_review_url: survey.google_review_url, already: true } : null);

  return (
    <div className="max-w-xl mx-auto px-6 pt-14 pb-36" data-testid="feedback-survey-page">
      {!done ? (
        <div className="eyw-card p-7 md:p-9">
          <p className="eyw-label mb-1">Quick feedback</p>
          <h1 className="font-serif text-2xl md:text-3xl text-[#1A242B]">How was {survey.subject_title || "your visit"}?</h1>
          <p className="text-[#4A5D6B] mt-2 text-sm">Thanks for being with us{survey.name ? `, ${survey.name.split(" ")[0]}` : ""}. Three quick questions — 30 seconds, promise.</p>
          <div className="mt-7 space-y-6">
            <div>
              <p className="text-sm font-medium text-[#1A242B] mb-2">1 · Overall, how was your experience?</p>
              <Stars value={experience} onChange={setExperience} testid="survey-experience" />
            </div>
            <div>
              <p className="text-sm font-medium text-[#1A242B] mb-2">2 · How likely are you to come back?</p>
              <Stars value={ret} onChange={setRet} testid="survey-return" />
            </div>
            <div>
              <p className="text-sm font-medium text-[#1A242B] mb-2">3 · Anything you'd like us to know? <span className="text-[#8FA89B] font-normal">(optional)</span></p>
              <textarea rows={3} value={comments} onChange={(e) => setComments(e.target.value)} data-testid="survey-comments" className="eyw-input w-full resize-none" placeholder="What stood out — or what could we do better?" />
            </div>
            <button onClick={submit} disabled={sending} data-testid="survey-submit" className="eyw-btn w-full justify-center disabled:opacity-60">{sending && <Loader2 className="animate-spin" size={16} />} Send feedback</button>
          </div>
        </div>
      ) : done.positive ? (
        <div className="eyw-card p-7 md:p-9 text-center" data-testid="survey-thanks-positive">
          <CheckCircle2 size={44} className="text-[#5C8A72] mx-auto mb-3" />
          <h1 className="font-serif text-2xl text-[#1A242B]">Thank you — that made our day!</h1>
          <p className="text-[#4A5D6B] mt-2 text-sm">Would you share the love? A quick Google review helps others find their path to wellness.</p>
          {done.google_review_url && (
            <a href={done.google_review_url} target="_blank" rel="noreferrer" data-testid="survey-google-review" className="eyw-btn mt-5 inline-flex"><ExternalLink size={15} /> Leave a Google review</a>
          )}
          {!tDone && !survey?.testimonial_submitted ? (
            <div className="mt-8 text-left border-t border-[#E5E0D8] pt-6" data-testid="survey-testimonial-form">
              <p className="font-serif text-lg text-[#1A242B] flex items-center gap-2"><Heart size={16} className="text-[#D98E73]" /> Or share a testimonial on our site</p>
              <textarea rows={3} value={quote} onChange={(e) => setQuote(e.target.value)} data-testid="survey-testimonial-quote" className="eyw-input w-full resize-none mt-3" placeholder="A sentence or two about your experience…" />
              <input value={tName} onChange={(e) => setTName(e.target.value)} data-testid="survey-testimonial-name" className="eyw-input w-full mt-2" placeholder={`Display name (default: ${survey?.name || "A community member"})`} />
              <button onClick={sendTestimonial} disabled={tSaving} data-testid="survey-testimonial-submit" className="eyw-btn-ghost !py-2 text-sm mt-3 disabled:opacity-60">{tSaving && <Loader2 className="animate-spin" size={14} />} Submit testimonial</button>
              <p className="text-[11px] text-[#8FA89B] mt-2">Testimonials are reviewed by our team before appearing on the site.</p>
            </div>
          ) : (
            <p className="text-sm text-[#5C8A72] mt-6" data-testid="survey-testimonial-done">Your testimonial is with our team for a quick review. Thank you! 💙</p>
          )}
          <p className="mt-8"><Link to="/" className="text-sm text-[#144F75] hover:underline">Back to Enhancing Your World →</Link></p>
        </div>
      ) : (
        <div className="eyw-card p-7 md:p-9 text-center" data-testid="survey-thanks-private">
          <Heart size={40} className="text-[#D98E73] mx-auto mb-3" />
          <h1 className="font-serif text-2xl text-[#1A242B]">Thank you for your honesty.</h1>
          <p className="text-[#4A5D6B] mt-3 text-sm leading-relaxed">We're sorry this visit wasn't everything it should have been. Your feedback has gone directly to our care team, and someone will follow up personally to make it right.</p>
          <p className="mt-8"><Link to="/" className="text-sm text-[#144F75] hover:underline">Back to Enhancing Your World →</Link></p>
        </div>
      )}
    </div>
  );
}
